import { ErrorMessages, MessageField, SignUpSource } from '@/constants';
import { useGeo, useGlobalState } from '@/hooks';
import type { IUserInfo } from '@/interfaces';
import { EmailSyncGoogleApiScopes } from '@/modules/pmt/constants';
import { useEmailSyncContext } from '@/modules/pmt/context';
import { addEmailAccount } from '@/modules/pmt/fetchers';
import { userInfoModify } from '@/utils';
import type { OriginalUserInfo } from '@/utils/umsDataModify.utils';
import type { FC } from 'react';
import { GoogleLoginButton, type GoogleLoginButtonProps } from '../GoogleLoginButton';

type SuccessCallbackPayload = {
  accessToken: string;
  originalUserInfo: OriginalUserInfo;
  modifiedUserInfo: IUserInfo;
};

export interface ConnectGoogleButtonProps {
  size?: GoogleLoginButtonProps['size'];
  buttonText?: string;
  newsletterOptIn?: boolean;
  handleClick?: GoogleLoginButtonProps['handleClick'];
  onSuccess?: (payload: SuccessCallbackPayload) => void;
  // biome-ignore lint/suspicious/noExplicitAny: error shape from Google OAuth is unknown
  onFailure?: (e: any) => void;
  buttonComponent?: React.ComponentType<{ onClick: () => void }>;
}

export const ConnectGoogleButton: FC<ConnectGoogleButtonProps> = ({
  size,
  buttonText,
  newsletterOptIn = false,
  handleClick,
  onSuccess,
  onFailure,
  buttonComponent,
}) => {
  const { geoInfo } = useGeo();
  const { userInfo } = useGlobalState();
  const { setConnectEsFailedReason, setSyncingAccount } = useEmailSyncContext();

  const loginButtonProps: GoogleLoginButtonProps = {
    onOAuthError: (error) => {
      console.error({ error });
    },
    onOAuthSuccess: async (response) => {
      const { code, scope } = response;

      const systemLoginPayload = {
        scope,
        code,
        signUpSource: SignUpSource.ConsumerWeb,
        newsletterOptIn,
        country: userInfo?.country ?? geoInfo.country,
        redirectUri: 'postmessage',
      };

      try {
        const res = await addEmailAccount(systemLoginPayload);

        if (!res?.user_info) {
          throw new Error(ErrorMessages[MessageField.EMAIL_SYNC_CANNOT_AUTHORIZED].error);
        }

        const modifiedUserInfo = userInfoModify(res.user_info);

        // To show "Syncing in progress banner"
        // Ref: https://www.pivotaltracker.com/story/show/187111995
        res.user_info.email_sync_opt_in && setSyncingAccount(code);

        onSuccess?.({
          accessToken: res.access_token,
          originalUserInfo: res.user_info,
          modifiedUserInfo,
        });
        // biome-ignore lint/suspicious/noExplicitAny: catch clause
      } catch (e: any) {
        const errorCode = e?.errors?.code;
        setConnectEsFailedReason(errorCode);
        onFailure?.(e.response?.data);
      }
    },
    redirect_uri: 'postmessage',
    scope: EmailSyncGoogleApiScopes,
    size,
    handleClick,
    buttonComponent,
  };

  return <GoogleLoginButton {...loginButtonProps}>{buttonText}</GoogleLoginButton>;
};
